import React, { useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import api from '../services/api';

interface CreateDraftOrderModalProps {
    isOpen: boolean;
    onClose: () => void;
    onSuccess?: () => void;
}

const CreateDraftOrderModal: React.FC<CreateDraftOrderModalProps> = ({
    isOpen,
    onClose,
    onSuccess,
}) => {
    const [draftName, setDraftName] = useState('');
    const [submitting, setSubmitting] = useState(false);
    const [errorMessage, setErrorMessage] = useState('');

    useEffect(() => {
        if (!isOpen) {
            setDraftName('');
            setSubmitting(false);
            setErrorMessage('');
        }
    }, [isOpen]);

    if (!isOpen) {
        return null;
    }

    const closeButtonClassName = `selector-modal__close${submitting ? ' is-disabled' : ''}`;
    const secondaryButtonClassName = `button weka-secondary-btn selector-modal__button selector-modal__button--secondary${submitting ? ' is-disabled' : ''}`;
    const primaryButtonClassName = `button weka-primary-btn selector-modal__button selector-modal__button--primary${submitting ? ' is-disabled' : ''}`;

    const handleCreateDraft = async () => {
        const trimmedName = draftName.trim();
        if (trimmedName === '') {
            setErrorMessage('Please enter a draft order name.');
            return;
        }

        setSubmitting(true);
        setErrorMessage('');

        try {
            const response = await api.createDraftOrder({
                draft_name: trimmedName,
                mode: 'manual',
            });

            toast.success(response.message);
            onSuccess?.();
            onClose();
        } catch (error) {
            const message = error instanceof Error ? error.message : 'Failed to create draft order.';
            setErrorMessage(message);
            toast.error(message);
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div
            className="selector-modal-overlay"
            onClick={() => {
                if (!submitting) {
                    onClose();
                }
            }}
        >
            <div
                className="selector-modal"
                onClick={(event) => event.stopPropagation()}
                role="dialog"
                aria-modal="true"
                aria-labelledby="create-draft-order-modal-title"
            >
                <div className="selector-modal__header">
                    <div>
                        <div id="create-draft-order-modal-title" className="selector-modal__title">Create Draft Order</div>
                        <p className="selector-modal__subtitle">
                            Enter a draft order name to create a new empty draft. Products can be attached later from the product flow.
                        </p>
                    </div>
                    <button
                        type="button"
                        aria-label="Close"
                        disabled={submitting}
                        onClick={onClose}
                        className={closeButtonClassName}
                    >
                        &times;
                    </button>
                </div>

                <form
                    className="selector-modal__body"
                    onSubmit={(event) => {
                        event.preventDefault();
                        void handleCreateDraft();
                    }}
                >
                    {errorMessage !== '' && (
                        <div className="selector-modal__error" role="alert">
                            {errorMessage}
                        </div>
                    )}

                    <section className="selector-modal__section">
                        <div>
                            <div className="selector-modal__section-title">Draft Order Name</div>
                            <p className="selector-modal__section-hint">This name will be shown in your draft orders table.</p>
                        </div>
                        <input
                            type="text"
                            placeholder="e.g., Monthly Restock Draft"
                            value={draftName}
                            onChange={(event) => setDraftName(event.currentTarget.value)}
                            disabled={submitting}
                            className="selector-modal__input"
                            autoFocus
                        />
                    </section>

                    <div className="selector-modal__actions">
                        <button
                            type="button"
                            onClick={onClose}
                            disabled={submitting}
                            className={secondaryButtonClassName}
                        >
                            Cancel
                        </button>
                        <button
                            type="submit"
                            disabled={submitting}
                            className={primaryButtonClassName}
                        >
                            {submitting ? 'Creating...' : 'Create Draft'}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default CreateDraftOrderModal;
